import Swiper from 'swiper';

const initializedWrappers = new WeakSet();
const hintStates = new WeakMap();
const swipeHintClass = 'is-show-swipe-hint';
const swipeHintDuration = 4200;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const getFilterWrappers = (scope = document) => {
  if (!scope) {
    return [];
  }

  const wrappers = [];

  if (typeof scope.matches === 'function' && scope.matches('.filters-swiper-wr')) {
    wrappers.push(scope);
  }

  if (typeof scope.querySelectorAll === 'function') {
    scope.querySelectorAll('.filters-swiper-wr').forEach((wrapper) => {
      if (!wrappers.includes(wrapper)) {
        wrappers.push(wrapper);
      }
    });
  }

  return wrappers;
};

const throttle = (callback, delay) => {
  let timer = null;

  return (...args) => {
    if (timer !== null) {
      return;
    }

    timer = setTimeout(() => {
      callback(...args);
      timer = null;
    }, delay);
  };
};

const slidesOverflow = (swiper) => {
  if (!swiper?.el || !swiper.slides?.length) {
    return false;
  }

  const lastSlide = swiper.slides[swiper.slides.length - 1];
  const slidesWidth = lastSlide.offsetLeft + lastSlide.offsetWidth;

  return slidesWidth > swiper.el.offsetWidth;
};

const isElementVisible = (el) => {
  if (!el?.isConnected) {
    return false;
  }

  const rect = el.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
};

const canPlaySwipeHint = (state) => {
  if (!state?.swiper || state.swiper.destroyed || reducedMotionQuery.matches) {
    return false;
  }

  if (typeof state.swiper.update === 'function') {
    state.swiper.update();
  }

  return isElementVisible(state.el) && slidesOverflow(state.swiper);
};

const createSwipeHint = (el) => {
  const slider = el.querySelector('.filters-slider') || el;
  let hint = Array.from(slider.children).find((child) => child.classList.contains('filters-slider__swipe-hint'));

  if (!hint) {
    hint = document.createElement('span');
    hint.className = 'filters-slider__swipe-hint';
    hint.setAttribute('aria-hidden', 'true');

    const dot = document.createElement('span');
    dot.className = 'filters-slider__swipe-hint-dot';

    hint.append(dot);
    slider.append(hint);
  }

  return {
    hint,
    slider,
  };
};

const stopSwipeHint = (state) => {
  if (!state) {
    return;
  }

  if (state.slider.classList.contains(swipeHintClass)) {
    state.slider.classList.remove(swipeHintClass);
  }

  state.isPlaying = false;

  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
};

const markSwipeHintInteracted = (state) => {
  if (!state) {
    return;
  }

  state.hasInteracted = true;
  stopSwipeHint(state);
};

const playSwipeHint = (el, options = {}) => {
  const state = hintStates.get(el);

  if (!state || state.isPlaying || state.hasInteracted || !canPlaySwipeHint(state)) {
    return false;
  }

  if (state.hasShown && !options.force) {
    return false;
  }

  state.hasShown = true;
  state.isPlaying = true;
  state.slider.classList.remove(swipeHintClass);
  state.hint.getBoundingClientRect();
  state.slider.classList.add(swipeHintClass);

  state.timer = setTimeout(() => {
    stopSwipeHint(state);
  }, swipeHintDuration);

  return true;
};

const observeSwipeHint = (el) => {
  const state = hintStates.get(el);

  if (!state) {
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    setTimeout(() => {
      playSwipeHint(el);
    }, 500);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      requestAnimationFrame(() => {
        if (playSwipeHint(el)) {
          observer.unobserve(el);
        }
      });
    });
  }, {
    threshold: 0.45,
  });

  state.observer = observer;
  observer.observe(el);
};

const scrollSlideIntoView = (swiper, index) => {
  if (!swiper || swiper.destroyed || !slidesOverflow(swiper)) {
    return;
  }

  const slide = swiper.slides[index];

  if (!slide) {
    return;
  }

  const containerWidth = swiper.el.offsetWidth;
  const currentTranslate = swiper.translate;
  const slideLeft = slide.offsetLeft;
  const slideRight = slideLeft + slide.offsetWidth;
  const visibleLeft = -currentTranslate;
  const visibleRight = visibleLeft + containerWidth;

  if (slideLeft >= visibleLeft && slideRight <= visibleRight) {
    return;
  }

  const inset = 16;
  let newTranslate = slideLeft < visibleLeft
    ? -slideLeft + inset
    : -(slideRight - containerWidth + inset);

  newTranslate = Math.max(swiper.maxTranslate(), Math.min(swiper.minTranslate(), newTranslate));

  swiper.translateTo(newTranslate, 300);
};

const syncActiveButton = (buttons, activeButton) => {
  buttons.forEach((button) => {
    const isActive = button === activeButton;

    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
};

const initFiltersSliders = (scope = document) => {
  const wrappers = getFilterWrappers(scope);

  wrappers.forEach((el) => {
    if (initializedWrappers.has(el)) {
      return;
    }

    const swiperEl = el.querySelector('.swiper');
    const filterBtns = Array.from(el.querySelectorAll('.filters-slider__btn'));

    if (!swiperEl || !filterBtns.length) {
      return;
    }

    initializedWrappers.add(el);

    const swiper = swiperEl.swiper || new Swiper(swiperEl, {
      observer: true,
      observeParents: true,
      spaceBetween: 12,
      slidesPerView: 'auto',
      threshold: 4,
      swipeToSlide: true,
      focusableElements: 'input, select, option, textarea, video, label',
      touchStartPreventDefault: true,
      touchMoveStopPropagation: true,
      touchReleaseOnEdges: false,
      normalizeSlideIndex: false,
      watchOverflow: true,
      breakpoints: {
        641: {
          spaceBetween: 16,
        },
      },
    });

    const { hint, slider } = createSwipeHint(el);
    const hintState = {
      el,
      slider,
      hint,
      swiper,
      hasShown: false,
      hasInteracted: false,
      isPlaying: false,
      timer: null,
      observer: null,
    };

    hintStates.set(el, hintState);
    observeSwipeHint(el);

    const initialActiveButton = filterBtns.find((button) => button.classList.contains('is-active')) || filterBtns[0];
    syncActiveButton(filterBtns, initialActiveButton);

    filterBtns.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        markSwipeHintInteracted(hintState);

        if (!btn.classList.contains('is-active')) {
          syncActiveButton(filterBtns, btn);
        }

        scrollSlideIntoView(swiper, index);
      });
    });

    swiperEl.addEventListener('pointerdown', () => markSwipeHintInteracted(hintState), { passive: true });
    swiperEl.addEventListener('touchstart', () => markSwipeHintInteracted(hintState), { passive: true });
    swiperEl.addEventListener('wheel', () => markSwipeHintInteracted(hintState), { passive: true });

    if (typeof swiper.on === 'function') {
      swiper.on('touchStart', () => markSwipeHintInteracted(hintState));
      swiper.on('slideChange', () => markSwipeHintInteracted(hintState));
    }

    const checkEdge = () => {
      if (window.innerWidth > 768) {
        el.classList.remove('is-right-edge');
        return;
      }

      const rect = el.getBoundingClientRect();
      const distance = Math.abs(window.innerWidth - rect.right);

      el.classList.toggle('is-right-edge', distance <= 2);

      if (!hintState.hasShown) {
        requestAnimationFrame(() => {
          playSwipeHint(el);
        });
      }
    };

    window.addEventListener('resize', throttle(checkEdge, 100));
    checkEdge();
  });
};

const playFiltersSwipeHints = (scope = document, options = {}) => {
  const wrappers = getFilterWrappers(scope);

  wrappers.forEach((el) => {
    if (!hintStates.has(el)) {
      initFiltersSliders(el);
    }

    playSwipeHint(el, options);
  });
};

initFiltersSliders();
window.initFiltersSliders = initFiltersSliders;
window.playFiltersSwipeHints = playFiltersSwipeHints;
